import { forwardRef, useCallback, useMemo, useState } from 'react';
import { DebouncedInputSearch } from '@components/debounced-input-search';
import { Divider } from '@tremor/react';
import classNames from 'classnames';

import { CheckboxItem } from '../components/checkbox-item';
import { ClearButton } from '../components/clear-button';
import LazyNestedCheckboxOptionItem from '../components/lazy-nested-checkbox-option-item';
import { countIDs, filterByName, getItem, toggleSelection, updateParentStates } from '../utils';

import { NestedCheckboxItem, NestedCheckboxListProps } from './nested-checkbox-list.type';

export const NestedCheckboxList = forwardRef<HTMLDivElement, NestedCheckboxListProps>(
  (
    {
      data,
      onChange,
      selectedIds,
      clearText,
      clearable,
      selectAllId,
      canSelectAll,
      selectAllText,
      checkboxOptionClassName,
      customStyles,
      canSearch,
      searchText,
    },
    ref
  ) => {
    const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(new Set(selectedIds ?? []));
    const totalIds = useMemo(() => countIDs(data), [data]);
    const [allSelected, setAllSelected] = useState<boolean>(totalIds === localSelectedIds.size);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const filteredData = useMemo(() => filterByName(searchTerm, data), [searchTerm, data]);

    const handleSelection = useCallback(
      (id: string, selectChildren = false) => {
        const node = getItem(id, data);
        if (!node) {
          return;
        }
        const shouldBeSelected = !localSelectedIds.has(node.id);
        toggleSelection(node, shouldBeSelected, localSelectedIds);
        if (selectChildren) {
          node.children?.forEach((child) => {
            toggleSelection(child, shouldBeSelected, localSelectedIds);
          });
        }
        updateParentStates(node, data, localSelectedIds);
        setLocalSelectedIds(new Set(localSelectedIds));
        onChange(Array.from(localSelectedIds));
        setAllSelected(totalIds === localSelectedIds.size);
      },
      [data, localSelectedIds, onChange, totalIds]
    );

    const clearSelection = () => {
      setLocalSelectedIds(new Set());
      onChange([]);
      setSearchTerm('');
      setAllSelected(false);
    };

    const toggleAll = () => {
      data.forEach((item: NestedCheckboxItem) => {
        toggleSelection(item, !allSelected, localSelectedIds);
      });
      setAllSelected(totalIds === localSelectedIds.size);
      onChange(Array.from(localSelectedIds));
    };

    const handleSearch = (value: string) => {
      setSearchTerm(value.toLowerCase());
    };

    const isSelected = useCallback((id: string) => localSelectedIds.has(id), [localSelectedIds]);

    const showClearButton = clearable && clearText;
    const showSelectAllCheckbox = canSelectAll && selectAllText && selectAllId;
    const mergedCustomStyles = useMemo(() => ({ paddingLeft: '16px', ...customStyles }), [customStyles]);

    return (
      <div ref={ref}>
        {(canSearch || showClearButton) && (
          <header
            className={classNames('sticky xs:relative sm:sticky top-0 z-10 pt-4', checkboxOptionClassName, {
              'bg-primary': !checkboxOptionClassName,
            })}>
            <div className='mb-5 flex gap-4'>
              {canSearch && (
                <DebouncedInputSearch
                  onChange={handleSearch}
                  placeholder={searchText}
                  defaultValue={searchTerm}
                  variant={'dark'}
                  paddingClasses='shrink-0'
                  canDeleteSearch
                  className='w-full'
                />
              )}
              {showClearButton && (
                <ClearButton
                  itemsCount={localSelectedIds.size}
                  onClick={clearSelection}
                  label={clearText}
                  isFullSize={!canSearch}
                />
              )}
            </div>
            <Divider className='my-4' />
          </header>
        )}
        {showSelectAllCheckbox && (
          <CheckboxItem
            id={selectAllId}
            customStyles={mergedCustomStyles}
            isSelected={allSelected}
            onChange={toggleAll}
            label={selectAllText}
            isIndeterminated={totalIds > localSelectedIds.size && localSelectedIds.size !== 0}
          />
        )}
        {filteredData.map((item: NestedCheckboxItem) => (
          <LazyNestedCheckboxOptionItem
            key={item.id}
            item={item}
            onSelect={handleSelection}
            isSelected={isSelected}
            isExpanded={searchTerm !== ''}
            checkboxOptionClassName={checkboxOptionClassName}
            customStyles={customStyles}
          />
        ))}
      </div>
    );
  }
);

export default NestedCheckboxList;
